import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

// Helper to normalize hex colors to [r, g, b] arrays (0.0 to 1.0)
function normalizeColor(hexCode: number): [number, number, number] {
    return [
        ((hexCode >> 16) & 255) / 255,
        ((hexCode >> 8) & 255) / 255,
        (hexCode & 255) / 255
    ];
}

const simplexNoiseShader = `
vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}
vec4 mod289(vec4 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}
vec4 permute(vec4 x) {
    return mod289(((x*34.0)+1.0)*x);
}
vec4 taylorInvSqrt(vec4 r) {
  return 1.79284291400159 - 0.85373472095314 * r;
}
float snoise(vec3 v) {
  const vec2 C = vec2(1.0/6.0, 1.0/3.0);
  const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
  vec3 i  = floor(v + dot(v, C.yyy));
  vec3 x0 = v - i + dot(i, C.xxx);
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min(g.xyz, l.zxy);
  vec3 i2 = max(g.xyz, l.zxy);
  vec3 x1 = x0 - i1 + C.xxx;
  vec3 x2 = x0 - i2 + C.yyy;
  vec3 x3 = x0 - D.yyy;
  i = mod289(i);
  vec4 p = permute(permute(permute(
            i.z + vec4(0.0, i1.z, i2.z, 1.0))
          + i.y + vec4(0.0, i1.y, i2.y, 1.0))
          + i.x + vec4(0.0, i1.x, i2.x, 1.0));
  float n_ = 0.142857142857;
  vec3 ns = n_ * D.wyz - D.xzx;
  vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_);
  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);
  vec4 b0 = vec4(x.xy, y.xy);
  vec4 b1 = vec4(x.zw, y.zw);
  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));
  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww;
  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot(m*m, vec4(dot(p0,x0), dot(p1,x1), dot(p2,x2), dot(p3,x3)));
}
`;

const blendModesShader = `
vec3 blendNormal(vec3 base, vec3 blend) {
  return blend;
}
vec3 blendNormal(vec3 base, vec3 blend, float opacity) {
  return (blendNormal(base, blend) * opacity + base * (1.0 - opacity));
}
`;

const vertexShader = `
uniform vec2 resolution;
uniform float u_time;
uniform float u_active_colors[5];

struct U_global_S {
    vec2 noiseFreq;
    float noiseSpeed;
};
uniform U_global_S u_global;

struct U_vertDeform_S {
    float incline;
    float offsetTop;
    float offsetBottom;
    vec2 noiseFreq;
    float noiseAmp;
    float noiseSpeed;
    float noiseFlow;
    float noiseSeed;
};
uniform U_vertDeform_S u_vertDeform;

uniform vec3 u_baseColor;

struct WaveLayers_S {
    vec3 color;
    vec2 noiseFreq;
    float noiseSpeed;
    float noiseFlow;
    float noiseSeed;
    float noiseFloor;
    float noiseCeil;
};
const int u_waveLayers_length = 4;
uniform WaveLayers_S u_waveLayers[u_waveLayers_length];

varying vec3 v_color;

${simplexNoiseShader}
${blendModesShader}

void main() {
  vec2 uvNorm = uv * 2.0 - 1.0;
  float time = u_time * u_global.noiseSpeed;
  vec2 noiseCoord = resolution * uvNorm * u_global.noiseFreq;

  float tilt = resolution.y / 2.0 * uvNorm.y;
  float incline = resolution.x * uvNorm.x / 2.0 * u_vertDeform.incline;
  float offset = resolution.x / 2.0 * u_vertDeform.incline * mix(u_vertDeform.offsetBottom, u_vertDeform.offsetTop, uv.y);

  float noise = snoise(vec3(
    noiseCoord.x * u_vertDeform.noiseFreq.x + time * u_vertDeform.noiseFlow,
    noiseCoord.y * u_vertDeform.noiseFreq.y,
    time * u_vertDeform.noiseSpeed + u_vertDeform.noiseSeed
  )) * u_vertDeform.noiseAmp;

  noise *= 1.0 - pow(abs(uvNorm.y), 2.0);
  noise = max(0.0, noise);

  vec3 pos = vec3(
    position.x,
    position.y + tilt + incline + noise - offset,
    position.z
  );

  if (u_active_colors[0] == 1.0) {
    v_color = u_baseColor;
  } else {
    v_color = vec3(0.0, 0.0, 0.0);
  }

  for (int i = 0; i < u_waveLayers_length; i++) {
    if (u_active_colors[i + 1] == 1.0) {
      WaveLayers_S layer = u_waveLayers[i];
      float layer_noise = smoothstep(
        layer.noiseFloor,
        layer.noiseCeil,
        snoise(vec3(
          noiseCoord.x * layer.noiseFreq.x + time * layer.noiseFlow,
          noiseCoord.y * layer.noiseFreq.y,
          time * layer.noiseSpeed + layer.noiseSeed
        )) / 2.0 + 0.5
      );
      v_color = blendNormal(v_color, layer.color, pow(layer_noise, 4.0));
    }
  }
  gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
}
`;

const fragmentShader = `
uniform vec2 resolution;
uniform float u_noise_intensity;
uniform float u_time;
varying vec3 v_color;

float rand(vec2 n) { 
  return fract(sin(dot(n, vec2(12.9898, 4.1414))) * 43758.5453);
}

void main() {
  vec3 color = v_color;
  if (u_noise_intensity > 0.0) {
    vec2 st_noise = gl_FragCoord.xy / resolution.xy;
    float time_factor_noise = u_time * 0.000000001;
    float noise_val = rand(vec2(
        st_noise.x * 5.0 + time_factor_noise, 
        st_noise.y * 5.0 - time_factor_noise * 0.7
    )); 
    color += (noise_val - 0.5) * u_noise_intensity;
  }

  gl_FragColor = vec4(color, 1.0);
}
`;

interface MeshGradientBGProps {
    speed?: number;
}

const palette = [
    0xCDD5C6,
    0xFBF2ED,
    0xC0E0C6,
    0xFBDBD5,
    0x04BFBF
];
const basicTime = 0xffffff * Math.random();
const GradientMesh = ({ }: { }) => {
    const { size } = useThree();
    const materialRef = useRef<THREE.ShaderMaterial>(null);
    const sectionColors = useMemo(() => palette.map(normalizeColor), [palette]);

    const uniforms = useMemo(() => {
        const waveLayers = [];
        for (let i = 0; i < 4; i++) {
            const colorIndex = i + 1;
            waveLayers.push({
                color: new THREE.Vector3().fromArray(sectionColors[colorIndex] || [0, 0, 0]),
                noiseFreq: new THREE.Vector2(2 + colorIndex / 5, 3 + colorIndex / 5),
                noiseSpeed: 1 + 0.3 * colorIndex,
                noiseFlow: 1.5 + 0.3 * colorIndex,
                noiseSeed: 5 + 10 * colorIndex,
                noiseFloor: 0.0,
                noiseCeil: 0.73 + 0.07 * colorIndex
            });
        }

        return {
            u_time: { value: 0 },
            u_noise_intensity: { value: 0.04 },
            u_active_colors: { value: [1.0, 1.0, 1.0, 1.0, 1.0] },
            u_global: {
                value: {
                    noiseFreq: new THREE.Vector2(14e-5, 29e-5),
                    noiseSpeed: 5e-6
                }
            },
            u_vertDeform: {
                value: {
                    incline: 0.0,
                    offsetTop: -0.5,
                    offsetBottom: -0.5,
                    noiseFreq: new THREE.Vector2(1, 2),
                    noiseAmp: size.width > size.height ? size.height / 2 : size.width / 2,
                    noiseSpeed: 66.0,
                    noiseFlow: 3.0,
                    noiseSeed: 2 + Math.ceil(Math.random() * 7)
                }
            },
            u_baseColor: { value: new THREE.Vector3().fromArray(sectionColors[0]) },
            u_waveLayers: { value: waveLayers },
            resolution: { value: new THREE.Vector2(size.width, size.height) }
        };
    }, [size.width, size.height, sectionColors]);
    useFrame((state) => {
        if (materialRef.current) {
            materialRef.current.uniforms.u_time.value = basicTime + state.clock.getElapsedTime() * 1000;
        }
    });

    const density = [0.1, 0.12]; // Reduced density for web performance
    const xSegmentCount = Math.ceil(size.width * density[0]);
    const ySegmentCount = Math.ceil(size.height * density[1]);

    return (
        <mesh>
            <planeGeometry args={[size.width, size.height, xSegmentCount, ySegmentCount]} />
            <shaderMaterial
                ref={materialRef}
                fragmentShader={fragmentShader}
                vertexShader={vertexShader}
                uniforms={uniforms}
                transparent
            />
        </mesh>
    );
};

export const MeshGradientBG = React.memo(({  }: MeshGradientBGProps) => {
    return (
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            <Canvas
                orthographic
                camera={{ position: [0, 0, 1], zoom: 1 }}
                gl={{ antialias: true, alpha: true }}
                dpr={[1, 2]}
            >
                <GradientMesh />
            </Canvas>
        </div>
    );
});
